import { Platform, PermissionsAndroid, Linking } from "react-native";
import * as Location from "expo-location";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { CustomAlert } from "@/components/ThemedAlert";

/**
 * Storage key to persist whether the user explicitly declined the GPS startup prompt.
 */
export const STORAGE_KEY_GPS_DECLINED = "@memories/gps_permission_declined";

/**
 * Interface representing the detailed location permission and provider status.
 */
export interface ILocationPermissionStatus {
  granted: boolean;
  canAskAgain: boolean;
  status: "granted" | "denied" | "undetermined";
  gpsEnabled: boolean;
}

/**
 * Checks whether the device location service (GPS provider) is enabled on the hardware.
 * @returns {Promise<boolean>} True if location services are active or on web.
 */
export async function checkLocationServicesEnabled(): Promise<boolean> {
  try {
    return await Location.hasServicesEnabledAsync();
  } catch (err) {
    console.warn("[Location] Could not determine if location services are enabled:", err);
    // On web or platforms where check is unsupported, default to true
    return true;
  }
}

/**
 * Inspects current location permission and device GPS provider status.
 * @returns {Promise<ILocationPermissionStatus>} Full permission and GPS state.
 */
export async function checkLocationPermissions(): Promise<ILocationPermissionStatus> {
  const gpsEnabled = await checkLocationServicesEnabled();

  try {
    // 1. Android Native explicit check via PermissionsAndroid for maximum reliability
    if (Platform.OS === "android") {
      const fineGranted = await PermissionsAndroid.check(
        PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION
      );
      if (fineGranted) {
        return {
          granted: true,
          canAskAgain: true,
          status: "granted",
          gpsEnabled,
        };
      }
    }

    // 2. Cross-platform check via expo-location
    const res = await Location.getForegroundPermissionsAsync();
    return {
      granted: res.granted,
      canAskAgain: res.canAskAgain,
      status: res.status as "granted" | "denied" | "undetermined",
      gpsEnabled,
    };
  } catch (err) {
    console.warn("[Location] Error checking location permissions:", err);
    return {
      granted: false,
      canAskAgain: true,
      status: "undetermined",
      gpsEnabled,
    };
  }
}

/**
 * Requests location permissions from the operating system.
 * @param {boolean} [forcePrompt=false] - Whether to attempt opening settings if permanently denied.
 * @returns {Promise<ILocationPermissionStatus>} Updated permission status.
 */
export async function requestLocationPermissions(
  forcePrompt: boolean = false
): Promise<ILocationPermissionStatus> {
  const gpsEnabled = await checkLocationServicesEnabled();

  try {
    if (Platform.OS === "android") {
      const requestRes = await PermissionsAndroid.requestMultiple([
        PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
        PermissionsAndroid.PERMISSIONS.ACCESS_COARSE_LOCATION,
      ]);

      const fineResult = requestRes[PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION];
      const coarseResult = requestRes[PermissionsAndroid.PERMISSIONS.ACCESS_COARSE_LOCATION];

      const granted =
        fineResult === PermissionsAndroid.RESULTS.GRANTED ||
        coarseResult === PermissionsAndroid.RESULTS.GRANTED;

      const neverAskAgain =
        fineResult === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN ||
        coarseResult === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN;

      if (!granted && neverAskAgain && forcePrompt) {
        CustomAlert.alert(
          "Location Permission Denied",
          "Location access was previously disabled. Please allow location permissions in device settings to tag memories with GPS.",
          [
            { text: "Cancel", style: "cancel" },
            {
              text: "Open Settings",
              style: "default",
              onPress: () => Linking.openSettings(),
            },
          ]
        );
      }

      return {
        granted,
        canAskAgain: !neverAskAgain,
        status: granted ? "granted" : "denied",
        gpsEnabled,
      };
    }

    const res = await Location.requestForegroundPermissionsAsync();

    if (!res.granted && !res.canAskAgain && forcePrompt && Platform.OS !== "web") {
      CustomAlert.alert(
        "Location Permission Denied",
        "Location permission is currently disabled. Please enable it in your device settings to use GPS features.",
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Open Settings",
            style: "default",
            onPress: () => Linking.openSettings(),
          },
        ]
      );
    }

    return {
      granted: res.granted,
      canAskAgain: res.canAskAgain,
      status: res.status as "granted" | "denied" | "undetermined",
      gpsEnabled,
    };
  } catch (err) {
    console.warn("[Location] Error requesting location permissions:", err);
    return {
      granted: false,
      canAskAgain: false,
      status: "denied",
      gpsEnabled,
    };
  }
}

/**
 * Checks if the user previously said 'No' to the startup GPS permission prompt.
 * @returns {Promise<boolean>} True if user previously declined.
 */
export async function getStartupGpsPromptDeclined(): Promise<boolean> {
  try {
    const val = await AsyncStorage.getItem(STORAGE_KEY_GPS_DECLINED);
    return val === "true";
  } catch (err) {
    console.warn("[Location] Failed to read startup GPS prompt declined state:", err);
    return false;
  }
}

/**
 * Sets whether the user declined the startup GPS permission prompt.
 * @param {boolean} declined - True if user declined.
 */
export async function setStartupGpsPromptDeclined(declined: boolean): Promise<void> {
  try {
    if (declined) {
      await AsyncStorage.setItem(STORAGE_KEY_GPS_DECLINED, "true");
    } else {
      await AsyncStorage.removeItem(STORAGE_KEY_GPS_DECLINED);
    }
  } catch (err) {
    console.warn("[Location] Failed to save startup GPS prompt declined state:", err);
  }
}

/**
 * Checks whether GPS permission should be requested on startup.
 * If permission does not exist and the user has not previously declined, prompts the user.
 * If the user says no, persists that choice so they are never prompted again on startup.
 */
export async function promptStartupGpsPermissionIfNeeded(): Promise<void> {
  try {
    const status = await checkLocationPermissions();
    if (status.granted) {
      return;
    }

    const alreadyDeclined = await getStartupGpsPromptDeclined();
    if (alreadyDeclined) {
      return;
    }

    // Prompt user on startup
    CustomAlert.alert(
      "Enable Location Services",
      "Memories uses GPS to automatically tag your memories, media, and location pins with precise coordinates. Would you like to enable location access?",
      [
        {
          text: "No",
          style: "cancel",
          onPress: () => {
            setStartupGpsPromptDeclined(true);
          },
        },
        {
          text: "Enable Location",
          style: "default",
          onPress: async () => {
            const result = await requestLocationPermissions(true);
            if (!result.granted) {
              // If user denied in system dialog, record as declined so we don't nag on startup
              await setStartupGpsPromptDeclined(true);
            }
          },
        },
      ],
      { cancelable: false }
    );
  } catch (err) {
    console.warn("[Location] Error in promptStartupGpsPermissionIfNeeded:", err);
  }
}
